import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { get, post } from '../Services/http';
import { EditorStringModel } from '../Types/EditorString';
import Product from '../Types/Product';

interface WishListState {
  items: WishListItem[];
  addToWishListAnimating: boolean;
}

export interface WishListProps {
  id: string;
  title?: string;
  type?: string;
  description?: EditorStringModel;
  items?: WishListItem[];
}

export interface WishListItem {
  id: string;
  listId: string;
  variantId: string;
  sortIndex: string;
  quantity: number;
  product: Product;
}

const handleApiResponse = async (response: Response) => {
  if (!response.ok) {
    throw new Error('API request failed');
  }
  return await response.json();
};

const DEFAULT_STATE: WishListState = {
  items: [],
  addToWishListAnimating: false,
};

const mapToAddToWishListItem = (item: Product) => {
  return { articleNumber: item.id, quantity: 1 };
};

export const addProductToWishList = createAsyncThunk(
  'wishlist/add',
  async (item: Product) => {
    const response = await post(
      '/api/wishlist/add',
      mapToAddToWishListItem(item)
    );
    return await response.json();
  }
);

export const updateWishlist = createAsyncThunk(
  'wishlist/update',
  async (state: WishListProps) => {
    const response = await post('/api/wishlist/update', state);
    return handleApiResponse(response);
  }
);

export const removeProductFromWishList = createAsyncThunk(
  'wishlist/remove',
  async (item: Product) => {
    const response = await post(
      '/api/wishlist/remove',
      mapToAddToWishListItem(item)
    );
    return await response.json();
  }
);

export const loadWishList = createAsyncThunk('wishlist/load', async (_, { dispatch }) => {
  const response = await get('/api/wishlist');
  const data = await handleApiResponse(response);
  dispatch(receiveWishList(data));
  return data;
});

export const addToCart = createAsyncThunk('wishlist/addToCart', async () => {
  const response = await post('/api/wishlist/addtocart');
  return handleApiResponse(response);
});

const WishListSlice = createSlice({
    name: 'wishList',
    initialState: DEFAULT_STATE,
    reducers: {
        removeWishListItem: (
            state,
            action: PayloadAction<{ product: Product }>
        ) => {
            state.items = state.items.filter(
                (item) => item.product.id !== action.payload.product.id
            );
        },
        addWishListItem: (
            state,
            action: PayloadAction<{ product: Product }>
        ) => {
            state.items.push({
                id: action.payload.id,
                listId: action.payload.listId,
                variantId: action.payload.variantId,
                sortIndex: action.payload.sortIndex,
                quantity: 1,
                product: action.payload.product,
            });
        },
        receiveWishList: (state, action: PayloadAction<WishListProps>) => {
            return {
                ...state,
                ...action.payload
            };
        }
    },
});

export const { removeWishListItem, addWishListItem, receiveWishList } = WishListSlice.actions;

export default WishListSlice.reducer;